import React, { useState, useCallback, memo } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import soundManager from '../utils/sounds';
import { usePlayerProgress } from '../contexts/PlayerContext';
import { PATH_TO_UNLOCK, UNLOCK_LEVEL_MAP } from '../constants/levelUnlocks';

// Icon Components
const Icons = {
  Home: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9,22 9,12 15,12 15,22"/>
    </svg>
  ),
  Star: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26"/>
    </svg>
  ),
  Clock: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
    </svg>
  ),
  Sparkle: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 3l1.5 5.5L19 10l-5.5 1.5L12 17l-1.5-5.5L5 10l5.5-1.5L12 3z"/>
      <path d="M5 3l.5 2L7 5.5 5.5 6 5 8l-.5-2L3 5.5 4.5 5 5 3z"/>
      <path d="M19 17l.5 2 1.5.5-1.5.5-.5 2-.5-2-1.5-.5 1.5-.5.5-2z"/>
    </svg>
  ),
  Settings: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="3"/><path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83"/>
    </svg>
  ),
  ChevronDown: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" className="chevron">
      <polyline points="6,9 12,15 18,9"/>
    </svg>
  ),
  Tower: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4 21V10l8-7 8 7v11"/><rect x="9" y="13" width="6" height="8"/>
    </svg>
  ),
  Scissors: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="6" cy="6" r="3"/><circle cx="6" cy="18" r="3"/><line x1="20" y1="4" x2="8.12" y2="15.88"/><line x1="14.47" y1="14.48" x2="20" y2="20"/><line x1="8.12" y1="8.12" x2="12" y2="12"/>
    </svg>
  ),
  Wheel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/><line x1="12" y1="2" x2="12" y2="9"/><line x1="12" y1="15" x2="12" y2="22"/>
    </svg>
  ),
  Dice: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5" fill="currentColor"/><circle cx="15.5" cy="15.5" r="1.5" fill="currentColor"/>
    </svg>
  ),
  Drop: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M12 2.69l5.66 5.66a8 8 0 1 1-11.31 0z"/>
    </svg>
  ),
  Mines: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="6"/><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/>
    </svg>
  ),
  Cards: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="2" y="4" width="14" height="16" rx="2"/><rect x="8" y="2" width="14" height="16" rx="2"/>
    </svg>
  ),
  Grid: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
    </svg>
  ),
  Rocket: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/><path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/><path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/><path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
    </svg>
  ),
  Roulette: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3" fill="currentColor"/>
    </svg>
  ),
  Chicken: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <ellipse cx="12" cy="14" rx="7" ry="6"/><circle cx="16" cy="8" r="4"/><polygon points="19,7 23,9 19,11" fill="currentColor"/><circle cx="17" cy="7" r="1" fill="currentColor"/>
    </svg>
  ),
  Trophy: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6"/><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18"/>
      <path d="M4 22h16"/><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20.24 7 22"/>
      <path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20.24 17 22"/>
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z"/>
    </svg>
  ),
  Calendar: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  ),
  Leaderboard: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
    </svg>
  ),
  Hilo: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M7 17L17 7M7 7h10v10"/>
    </svg>
  ),
  Users: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  Lock: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
    </svg>
  ),
};

// Hoisted static data - prevents recreation on every render
const MAIN_NAV_ITEMS = [
  { name: 'Home', tKey: 'home', icon: 'Home', path: '/' },
  { name: 'Achievements', tKey: 'achievements', icon: 'Trophy', path: '/achievements' },
];

const SIDEBAR_MULTIPLAYER = [
  { name: 'Crash', tKey: 'game_crash', icon: 'Rocket', path: '/crash' },
];

const SIDEBAR_ORIGINALS = [
  { name: 'Tower', tKey: 'game_tower', icon: 'Tower', path: '/tower' },
  { name: 'Chicken Cross', tKey: 'game_chicken_cross', icon: 'Chicken', path: '/chicken' },
  { name: 'Wheel', tKey: 'game_wheel', icon: 'Wheel', path: '/wheel' },
  { name: 'Dice', tKey: 'game_dice', icon: 'Dice', path: '/dice' },
  { name: 'Plinko', tKey: 'game_plinko', icon: 'Drop', path: '/plinko', isNew: true },
  { name: 'Limbo', tKey: 'game_limbo', icon: 'Drop', path: '/limbo' },
  { name: 'Mines', tKey: 'game_mines', icon: 'Mines', path: '/mines' },
  { name: 'Keno', tKey: 'game_keno', icon: 'Grid', path: '/keno' },
  { name: 'HiLo', tKey: 'game_hilo', icon: 'Hilo', path: '/hilo' },
];

const renderIcon = (iconName) => {
  const IconComponent = Icons[iconName];
  return IconComponent ? <IconComponent /> : null;
};

// Hover-preload map: route path -> lazy chunk import
const PRELOAD_MAP = {
  '/': () => import('../games/Homepage'),
  '/achievements': () => import('../games/Achievements'),
  '/crash': () => import('../games/Crash'),
  '/tower': () => import('../games/Tower'),
  '/chicken': () => import('../games/ChickenCross'),
  '/wheel': () => import('../games/Wheel'),
  '/dice': () => import('../games/Dice'),
  '/plinko': () => import('../games/Plinko'),
  '/limbo': () => import('../games/Limbo'),
  '/mines': () => import('../games/Mines'),
  '/keno': () => import('../games/Keno'),
  '/hilo': () => import('../games/HiLo'),
};

const preloadedRoutes = new Set();
const handlePreload = (path) => {
  if (preloadedRoutes.has(path)) return;
  const loader = PRELOAD_MAP[path];
  if (loader) {
    preloadedRoutes.add(path);
    loader();
  }
};

// Check if a game path is unlocked
function isPathUnlocked(path, unlockedItems) {
  const unlockId = PATH_TO_UNLOCK[path];
  if (!unlockId) return true; // no lock requirement
  return unlockedItems.includes(unlockId);
}

function getRequiredLevel(path) {
  const unlockId = PATH_TO_UNLOCK[path];
  if (!unlockId) return 0;
  return UNLOCK_LEVEL_MAP[unlockId] || 0;
}

const GameNavItem = memo(({ item, unlockedItems, onClick, t }) => {
  const unlocked = isPathUnlocked(item.path, unlockedItems);
  const requiredLevel = getRequiredLevel(item.path);

  if (!unlocked) {
    return (
      <div
        className="sidebar-item sidebar-item-locked"
        title={t('unlock_at_level', { level: requiredLevel })}
      >
        <span className="icon-wrapper" style={{ opacity: 0.3 }}>
          {renderIcon(item.icon)}
        </span>
        <span style={{ opacity: 0.3 }}>{item.tKey ? t(item.tKey) : item.name}</span>
        <span className="lock-badge">
          <Icons.Lock /> {requiredLevel}
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={item.path}
      className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
      onClick={onClick}
      onMouseEnter={() => handlePreload(item.path)}
    >
      <span className="icon-wrapper">
        {renderIcon(item.icon)}
      </span>
      {item.tKey ? t(item.tKey) : item.name}
      {item.isNew && <span className="title-badge">{t('new')}</span>}
    </NavLink>
  );
});

const Sidebar = () => {
  const { t } = useTranslation('common');
  const [multiplayerExpanded, setMultiplayerExpanded] = useState(true);
  const [originalsExpanded, setOriginalsExpanded] = useState(true);
  const { unlockedItems } = usePlayerProgress();

  const handleNavClick = useCallback(() => soundManager.playClick(), []);
  const handleToggleMultiplayer = useCallback(() => {
    soundManager.playClick();
    setMultiplayerExpanded(prev => !prev);
  }, []);
  const handleToggleOriginals = useCallback(() => {
    soundManager.playClick();
    setOriginalsExpanded(prev => !prev);
  }, []);

  return (
    <aside className="left-sidebar">
      {/* Main Navigation */}
      <div className="sidebar-section">
        {MAIN_NAV_ITEMS.map(item => (
          <NavLink
            key={item.name}
            to={item.path}
            className={({ isActive }) => `sidebar-item ${isActive ? 'active' : ''}`}
            onClick={handleNavClick}
            onMouseEnter={() => handlePreload(item.path)}
          >
            <span className="icon-wrapper">
              {renderIcon(item.icon)}
            </span>
            {item.tKey ? t(item.tKey) : item.name}
            {item.isHot && <span className="title-badge hot">{t('hot')}</span>}
          </NavLink>
        ))}
      </div>

      {/* Section Divider */}
      <div className="section-divider" />

      {/* Multiplayer Section */}
      <button
        className={`section-header ${multiplayerExpanded ? 'expanded' : ''}`}
        onClick={handleToggleMultiplayer}
      >
        <div className="section-header-left">
          <span className="section-icon multiplayer-icon">
            <Icons.Users />
          </span>
          {t('multiplayer')}
        </div>
        <Icons.ChevronDown />
      </button>

      {multiplayerExpanded && (
        <div className="multiplayer-list">
          {SIDEBAR_MULTIPLAYER.map(item => (
            <GameNavItem key={item.name} item={item} unlockedItems={unlockedItems} onClick={handleNavClick} t={t} />
          ))}
        </div>
      )}

      {/* Section Divider */}
      <div className="section-divider" />

      {/* Originals Section */}
      <button
        className={`section-header ${originalsExpanded ? 'expanded' : ''}`}
        onClick={handleToggleOriginals}
      >
        <div className="section-header-left">
          <span className="section-icon">
            <Icons.Settings />
          </span>
          {t('originals')}
        </div>
        <Icons.ChevronDown />
      </button>

      {originalsExpanded && (
        <div className="originals-list">
          {SIDEBAR_ORIGINALS.map(item => (
            <GameNavItem key={item.name} item={item} unlockedItems={unlockedItems} onClick={handleNavClick} t={t} />
          ))}
        </div>
      )}
    </aside>
  );
};

export default memo(Sidebar);
